"use client";

import React, { useState } from "react";
import Link from "next/link";
import {
  CheckCircle2, ChevronDown, ArrowRight, Guitar,
  Shield, Star, Zap, Music, Search, Award,
  MapPin, Clock, Phone, Layers, Radio, Waves,
} from "lucide-react";

/* ─────────────────────────────────────────
   DATA
───────────────────────────────────────── */

const BENEFITS = [
  {
    icon: Zap,
    title: "Tiết kiệm 40–60% so với đàn mới",
    desc: "Cùng mức ngân sách, bạn có thể sở hữu cây đàn thương hiệu uy tín thay vì đàn mới rẻ tiền chất lượng kém.",
  },
  {
    icon: Music,
    title: "Âm thanh đã \"chín\" tự nhiên",
    desc: "Gỗ qua thời gian rung động liên tục cho âm thanh cộng hưởng ổn định, ấm và sâu hơn đàn mới cùng chất liệu.",
  },
  {
    icon: Shield,
    title: "Khấu hao thấp – dễ bán lại",
    desc: "Đàn cũ đã qua giai đoạn khấu hao nhanh. Bạn có thể bán lại hoặc đổi lên đàn tốt hơn mà không mất nhiều.",
  },
  {
    icon: Award,
    title: "Thương hiệu tầm trung dễ tiếp cận",
    desc: "Yamaha, Takamine, Morris, Fender… những thương hiệu tốt vốn ngoài tầm ngân sách giờ hoàn toàn trong tầm tay.",
  },
  {
    icon: Search,
    title: "Đã qua kiểm tra thực tế",
    desc: "Người dùng trước đã test và vận hành, giúp phát hiện lỗi tiềm ẩn sớm hơn so với đàn mới chưa qua sử dụng.",
  },
  {
    icon: Star,
    title: "Lý tưởng cho người mới bắt đầu",
    desc: "Không cần đầu tư lớn ngay từ đầu. Học đủ vững rồi nâng cấp — đây là chiến lược thông minh của nhiều guitarist.",
  },
];

const CATEGORIES = [
  {
    icon: Waves,
    name: "Guitar Acoustic",
    slug: "guitar-acoustic-cu",
    tag: "Phổ biến nhất",
    tagColor: "var(--brand-primary)",
    desc: "Thân gỗ lớn, âm vang tự nhiên, không cần ampli. Phù hợp đệm hát, nhạc folk, pop ballad.",
    specs: ["Steel string", "Thân dreadnought / concert", "Yamaha, Takamine, Fender"],
    highlight: "Dễ kiếm, đa dạng giá từ 1–5 triệu",
  },
  {
    icon: Music,
    name: "Guitar Classic",
    slug: "guitar-classic-cu",
    tag: "Nhẹ nhàng nhất",
    tagColor: "#22c55e",
    desc: "Dây nylon, tiếng ấm và mềm mại. Phù hợp nhạc cổ điển, bossa nova, người mới học.",
    specs: ["Nylon string", "Thân 4/4 hoặc 3/4", "Yamaha, Córdoba, Takamine"],
    highlight: "Lý tưởng cho học sinh, người mới",
  },
  {
    icon: Radio,
    name: "Guitar Điện",
    slug: "guitar-dien-cu",
    tag: "Đa năng nhất",
    tagColor: "#fb2150",
    desc: "Cần ampli để phát âm đầy đủ. Phù hợp rock, blues, jazz và các thể loại hiện đại.",
    specs: ["Humbucker / Single coil", "Stratocaster, Les Paul body", "Fender, Squier, Epiphone"],
    highlight: "Thị trường second-hand sôi động",
  },
  {
    icon: Layers,
    name: "Guitar Điện – Acoustic",
    slug: "guitar-electro-acoustic-cu",
    tag: "Tiện lợi nhất",
    tagColor: "#8b5cf6",
    desc: "Có thể dùng không dây hoặc cắm ampli/soundcard. Lý tưởng cho biểu diễn sân khấu nhỏ.",
    specs: ["Built-in pickup/EQ", "Cổng jack 6.3mm", "Takamine, Yamaha APX, Fender"],
    highlight: "Hai trong một — linh hoạt tối đa",
  },
];

const CHECKLIST = [
  {
    step: "01",
    title: "Kiểm tra thân đàn & mặt đàn",
    detail:
      "Soi kỹ toàn bộ bề mặt gỗ dưới ánh sáng mạnh. Tìm vết nứt nhỏ ở mặt đàn (top), hông (side) và lưng (back). Vết nứt dù nhỏ cũng ảnh hưởng âm thanh và có thể lan rộng theo thời gian.",
    warning: "Từ chối nếu có vết nứt qua mặt đàn gần soundhole.",
  },
  {
    step: "02",
    title: "Kiểm tra độ thẳng cần đàn (neck)",
    detail:
      "Nhìn dọc từ đầu đàn (headstock) xuống body theo đường thẳng. Cần đàn phải thẳng hoặc hơi cong nhẹ về phía trước (relief). Cong ngược (backbow) hoặc cong lệch bên là lỗi nghiêm trọng.",
    warning: "Cần cong nhiều = có thể phải thay truss rod, chi phí cao.",
  },
  {
    step: "03",
    title: "Đo action (độ cao dây so với phím)",
    detail:
      "Dùng thước hoặc ước lượng tại phím 12: dây 1 (mỏng) khoảng 1.5–2mm, dây 6 (bass) khoảng 2–2.5mm. Action quá cao = đau tay, khó bấm. Action quá thấp = buzz âm.",
    warning: "Action quá cao thường là dấu hiệu cần đàn đang cong hoặc saddle quá cao.",
  },
  {
    step: "04",
    title: "Kiểm tra phím đàn (frets)",
    detail:
      "Vuốt tay dọc theo mép phím — phím tốt không có cạnh sắc nhô ra. Quan sát mặt trên phím tìm vết mòn lõm (đặc biệt phím 1–5). Phím mòn nặng cần fret job tốn 300–500k.",
    warning: "Phím lõm sâu ở nhiều vị trí = cần thay toàn bộ phím đàn.",
  },
  {
    step: "05",
    title: "Kiểm tra nut & saddle",
    detail:
      "Nut là thanh ngà/nhựa ở đầu cần đàn. Saddle là thanh trắng ở bridge. Cả hai phải nguyên vẹn, không nứt. Rãnh nut không quá sâu (gây buzz ở phím hở) hay quá cao (đau tay ở đầu cần).",
    warning: "Nut nhựa rẻ tiền nên thay bằng bone nut để cải thiện âm thanh.",
  },
  {
    step: "06",
    title: "Kiểm tra dây chốt & máy lên dây",
    detail:
      "Với acoustic: nhấn nhẹ từng dây chốt ở bridge — nếu lỏng là dấu hiệu bridge đang tách. Với điện: kiểm tra từng chốt tremolo và nut locking. Máy lên dây phải xoay trơn, không rơ.",
    warning: "Bridge tách ra khỏi top = sửa chữa đắt tiền, cần thợ lành nghề.",
  },
  {
    step: "07",
    title: "Nghe âm thanh thực tế – test đủ vị trí phím",
    detail:
      "Chơi từng nốt ở tất cả 12 phím đầu tiên trên mỗi dây. Nghe xem có buzz bất thường không. Đánh một số hợp âm đơn giản. Với đàn điện: cắm vào amp và test ở volume vừa để nghe pickup.",
    warning: "Buzz chỉ ở 1–2 vị trí thường do phím mòn điểm, có thể leveling. Buzz toàn bộ = vấn đề lớn hơn.",
  },
];

/* ─────────────────────────────────────────
   SUB-COMPONENTS
───────────────────────────────────────── */

const SectionBadge = ({ children }: { children: React.ReactNode }) => (
  <div
    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold border mb-5"
    style={{
      backgroundColor: "rgba(200,169,110,0.08)",
      borderColor: "rgba(200,169,110,0.35)",
      color: "var(--brand-primary)",
      fontFamily: "var(--font-montserrat)",
      letterSpacing: "0.08em",
      textTransform: "uppercase",
    }}
  >
    {children}
  </div>
);

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <h2
    className="text-2xl sm:text-3xl md:text-4xl font-black leading-tight tracking-tight mb-4"
    style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
  >
    {children}
  </h2>
);

const Divider = () => (
  <div className="flex items-center justify-center gap-3 mt-6 mb-14">
    <div className="h-px w-16 rounded-full" style={{ backgroundColor: "rgba(200,169,110,0.2)" }} />
    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "var(--brand-primary)" }} />
    <div className="h-px w-16 rounded-full" style={{ backgroundColor: "rgba(200,169,110,0.2)" }} />
  </div>
);

/* ─────────────────────────────────────────
   CHECKLIST ITEM
───────────────────────────────────────── */
const ChecklistItem = ({ item, index }: { item: typeof CHECKLIST[0]; index: number }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="rounded-2xl border overflow-hidden transition-all duration-200"
      style={{
        backgroundColor: "var(--card)",
        borderColor: open ? "rgba(200,169,110,0.45)" : "var(--border)",
        boxShadow: open ? "0 4px 24px rgba(200,169,110,0.07)" : "none",
      }}
    >
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-4 px-5 py-4 text-left"
        aria-expanded={open}
      >
        {/* Step number */}
        <div
          className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-xs font-black transition-all duration-150"
          style={{
            fontFamily: "var(--font-montserrat)",
            backgroundColor: open ? "var(--brand-primary)" : "rgba(200,169,110,0.1)",
            color: open ? "var(--brand-primary-foreground)" : "var(--brand-primary)",
            border: open ? "none" : "1.5px solid rgba(200,169,110,0.25)",
          }}
        >
          {item.step}
        </div>

        <h3
          className="flex-1 font-black text-sm sm:text-base leading-snug"
          style={{
            fontFamily: "var(--font-montserrat)",
            color: "var(--foreground)",
          }}
        >
          {item.title}
        </h3>

        {open
          ? <CheckCircle2 className="w-5 h-5 shrink-0" style={{ color: "var(--brand-primary)" }} />
          : <ChevronDown className="w-5 h-5 shrink-0" style={{ color: "var(--muted-foreground)" }} />
        }
      </button>

      {open && (
        <div style={{ borderTop: "1px solid rgba(200,169,110,0.12)" }}>
          <div className="px-5 pt-4 pb-2 flex gap-4">
            <div className="shrink-0 w-0.5 self-stretch rounded-full" style={{ backgroundColor: "rgba(200,169,110,0.3)" }} />
            <p className="text-sm leading-relaxed" style={{ color: "var(--muted-foreground)" }}>
              {item.detail}
            </p>
          </div>
          <div
            className="mx-5 mb-5 mt-3 flex items-start gap-2.5 px-4 py-3 rounded-xl border"
            style={{
              backgroundColor: "rgba(251,33,80,0.06)",
              borderColor: "rgba(251,33,80,0.2)",
            }}
          >
            <span className="text-base shrink-0 mt-0.5">⚠️</span>
            <p className="text-xs leading-relaxed" style={{ color: "#fca5a5" }}>
              {item.warning}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────── */
export default function GuitarDaQuaSuDungClient() {
  return (
    <main
      itemScope
      itemType="https://schema.org/WebPage"
      style={{
        backgroundColor: "var(--background)",
        color: "var(--foreground)",
        fontFamily: "var(--font-roboto)",
      }}
    >

      {/* ══════════════════════════════════════
          HERO
      ══════════════════════════════════════ */}
      <section
        className="relative overflow-hidden pt-16 pb-20 px-4 sm:px-6 lg:px-8"
        aria-labelledby="hero-heading"
      >
        {/* Background texture */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(200,169,110,0.07) 0%, transparent 70%)",
        }} />
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: "radial-gradient(rgba(200,169,110,0.06) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }} />

        <div className="relative max-w-4xl mx-auto text-center">
          <SectionBadge>
            <Guitar className="w-3.5 h-3.5" />
            Guitar đã qua sử dụng · Quận 12 TPHCM
          </SectionBadge>

          <h1
            id="hero-heading"
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black leading-[1.1] tracking-tight mb-6"
            style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
          >
            Guitar Đã Qua{" "}
            <span className="relative inline-block" style={{ color: "var(--brand-primary)" }}>
              Sử Dụng
              <span
                className="absolute left-0 -bottom-1 w-full h-0.5 rounded-full"
                style={{ backgroundColor: "var(--brand-primary)", opacity: 0.4 }}
              />
            </span>
            <br className="hidden sm:block" />
            <span style={{ color: "var(--foreground)" }}> — Giá Trị Thật,</span>{" "}
            <span style={{ color: "var(--brand-primary)" }}>Minh Bạch Thật</span>
          </h1>

          <p
            className="text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-10"
            style={{ color: "var(--muted-foreground)" }}
          >
            Mỗi cây đàn tại Guitar Sài Gòn đều qua{" "}
            <strong style={{ color: "var(--foreground)" }}>7 bước kiểm tra chuyên sâu</strong>{" "}
            trước khi đến tay bạn. Không mua qua ảnh, không cam kết mù quáng —{" "}
            <strong style={{ color: "var(--foreground)" }}>xem tận mặt, nghe tận tai</strong>.
          </p>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto mb-10">
            {[
              { value: "500+", label: "Cây đàn đã qua tay" },
              { value: "3 tháng", label: "Bảo hành rõ ràng" },
              { value: "100%", label: "Kiểm tra trực tiếp" },
            ].map(({ value, label }) => (
              <div
                key={label}
                className="flex flex-col items-center gap-1 px-3 py-4 rounded-2xl border"
                style={{ backgroundColor: "var(--card)", borderColor: "rgba(200,169,110,0.2)" }}
              >
                <span
                  className="text-xl sm:text-2xl font-black"
                  style={{ fontFamily: "var(--font-montserrat)", color: "var(--brand-primary)" }}
                >
                  {value}
                </span>
                <span className="text-xs text-center leading-snug" style={{ color: "var(--muted-foreground)" }}>
                  {label}
                </span>
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              href="/danh-muc/guitar-cu-second-hand"
              className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-black text-sm transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
              style={{
                fontFamily: "var(--font-montserrat)",
                backgroundColor: "var(--brand-primary)",
                color: "var(--brand-primary-foreground)",
                boxShadow: "0 4px 20px rgba(200,169,110,0.3)",
              }}
            >
              Xem tất cả đàn cũ
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              href="#checklist"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-bold text-sm border transition-all duration-150"
              style={{
                fontFamily: "var(--font-montserrat)",
                borderColor: "rgba(200,169,110,0.35)",
                color: "var(--brand-primary)",
                backgroundColor: "rgba(200,169,110,0.06)",
              }}
            >
              Checklist 7 bước kiểm tra
            </Link>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ══════════════════════════════════════
            BENEFITS
        ══════════════════════════════════════ */}
        <section className="py-16" aria-labelledby="benefits-heading">
          <div className="text-center mb-12">
            <SectionBadge>Vì sao chọn đàn đã qua sử dụng?</SectionBadge>
            <SectionHeading>
              6 Lợi Ích Bạn Chỉ Có Khi Mua{" "}
              <span style={{ color: "var(--brand-primary)" }}>Guitar Cũ</span>
            </SectionHeading>
            <p className="text-sm sm:text-base max-w-xl mx-auto" style={{ color: "var(--muted-foreground)" }}>
              Không phải đàn mới lúc nào cũng là lựa chọn tốt hơn. Đây là lý do người chơi thông minh thường chọn guitar đã qua sử dụng.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {BENEFITS.map(({ icon: Icon, title, desc }, i) => (
              <div
                key={i}
                className="group rounded-2xl border p-6 transition-all duration-200"
                style={{
                  backgroundColor: "var(--card)",
                  borderColor: "var(--border)",
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.borderColor = "rgba(200,169,110,0.4)";
                  el.style.transform = "translateY(-2px)";
                  el.style.boxShadow = "0 12px 32px rgba(0,0,0,0.15)";
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.borderColor = "var(--border)";
                  el.style.transform = "translateY(0)";
                  el.style.boxShadow = "none";
                }}
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 border"
                  style={{
                    backgroundColor: "rgba(200,169,110,0.1)",
                    borderColor: "rgba(200,169,110,0.25)",
                  }}
                >
                  <Icon className="w-5 h-5" style={{ color: "var(--brand-primary)" }} />
                </div>
                <h3
                  className="font-black text-sm mb-2 leading-snug"
                  style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
                >
                  {title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: "var(--muted-foreground)" }}>
                  {desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        <Divider />

        {/* ══════════════════════════════════════
            CATEGORIES
        ══════════════════════════════════════ */}
        <section className="pb-16" aria-labelledby="categories-heading">
          <div className="text-center mb-12">
            <SectionBadge>Phân loại đàn</SectionBadge>
            <SectionHeading>
              4 Loại Guitar Đã Qua Sử Dụng{" "}
              <span style={{ color: "var(--brand-primary)" }}>Phổ Biến Nhất</span>
            </SectionHeading>
            <p className="text-sm sm:text-base max-w-xl mx-auto" style={{ color: "var(--muted-foreground)" }}>
              Mỗi loại phù hợp với phong cách âm nhạc và nhu cầu khác nhau. Chọn đúng loại ngay từ đầu để không mua đi mua lại nhiều lần.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {CATEGORIES.map((cat, i) => {
              const Icon = cat.icon;
              return (
                <Link
                  key={i}
                  href={`/danh-muc/${cat.slug}`}
                  className="group block rounded-3xl border overflow-hidden transition-all duration-200"
                  style={{
                    backgroundColor: "var(--card)",
                    borderColor: "var(--border)",
                    textDecoration: "none",
                  }}
                  onMouseEnter={(e) => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.borderColor = "rgba(200,169,110,0.45)";
                    el.style.boxShadow = "0 16px 40px rgba(0,0,0,0.2)";
                  }}
                  onMouseLeave={(e) => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.borderColor = "var(--border)";
                    el.style.boxShadow = "none";
                  }}
                >
                  {/* Gold top bar */}
                  <div className="h-1" style={{ backgroundColor: cat.tagColor, opacity: 0.7 }} />

                  <div className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div
                        className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 border"
                        style={{
                          backgroundColor: `${cat.tagColor}15`,
                          borderColor: `${cat.tagColor}35`,
                        }}
                      >
                        <Icon className="w-5 h-5" style={{ color: cat.tagColor }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3
                            className="font-black text-base"
                            style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
                          >
                            {cat.name}
                          </h3>
                          <span
                            className="px-2 py-0.5 rounded-full text-xs font-bold"
                            style={{
                              backgroundColor: `${cat.tagColor}18`,
                              color: cat.tagColor,
                              fontFamily: "var(--font-montserrat)",
                            }}
                          >
                            {cat.tag}
                          </span>
                        </div>
                        <p className="text-sm leading-relaxed" style={{ color: "var(--muted-foreground)" }}>
                          {cat.desc}
                        </p>
                      </div>
                    </div>

                    <div
                      className="rounded-xl p-4 border mb-4"
                      style={{
                        backgroundColor: "var(--background)",
                        borderColor: "var(--border)",
                      }}
                    >
                      <div className="flex flex-wrap gap-2 mb-3">
                        {cat.specs.map((spec) => (
                          <span
                            key={spec}
                            className="px-2.5 py-1 rounded-lg text-xs font-medium border"
                            style={{
                              backgroundColor: "var(--card)",
                              borderColor: "var(--border)",
                              color: "var(--muted-foreground)",
                            }}
                          >
                            {spec}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--brand-primary)" }} />
                        <span className="text-xs font-semibold" style={{ color: "var(--brand-primary)", fontFamily: "var(--font-montserrat)" }}>
                          {cat.highlight}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5" style={{ color: "var(--brand-primary)" }}>
                      <span className="text-xs font-bold" style={{ fontFamily: "var(--font-montserrat)" }}>
                        Xem đàn {cat.name.toLowerCase()} cũ
                      </span>
                      <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        <Divider />

        {/* ══════════════════════════════════════
            CHECKLIST
        ══════════════════════════════════════ */}
        <section id="checklist" className="pb-16" aria-labelledby="checklist-heading">
          <div className="text-center mb-12">
            <SectionBadge>Không bị hớ khi mua đàn cũ</SectionBadge>
            <SectionHeading>
              Checklist{" "}
              <span style={{ color: "var(--brand-primary)" }}>7 Bước Kiểm Tra</span>
              <br className="hidden sm:block" />
              {" "}Guitar Đã Qua Sử Dụng
            </SectionHeading>
            <p className="text-sm sm:text-base max-w-xl mx-auto" style={{ color: "var(--muted-foreground)" }}>
              Dù mua ở đâu — kể cả tại Guitar Sài Gòn — hãy tự kiểm tra theo 7 bước này trước khi quyết định. Nhấn vào từng bước để xem chi tiết.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="space-y-3 mb-10">
              {CHECKLIST.map((item, i) => (
                <ChecklistItem key={i} item={item} index={i} />
              ))}
            </div>

            {/* CTA after checklist */}
            <div
              className="rounded-3xl border p-8 text-center relative overflow-hidden"
              style={{
                backgroundColor: "var(--card)",
                borderColor: "rgba(200,169,110,0.3)",
                boxShadow: "0 0 60px rgba(200,169,110,0.06)",
              }}
            >
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: "radial-gradient(ellipse at center, rgba(200,169,110,0.06) 0%, transparent 70%)",
                }}
              />
              <div className="relative">
                <div className="h-1 w-10 rounded-full mx-auto mb-6" style={{ backgroundColor: "var(--brand-primary)" }} />
                <h3
                  className="text-xl sm:text-2xl font-black mb-3"
                  style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
                >
                  Không muốn tự kiểm tra một mình?
                </h3>
                <p className="text-sm sm:text-base mb-6 max-w-md mx-auto" style={{ color: "var(--muted-foreground)" }}>
                  Mang đàn đến Guitar Sài Gòn — Cường Guitar sẽ kiểm tra toàn bộ 7 bước cho bạn,{" "}
                  <strong style={{ color: "var(--foreground)" }}>miễn phí</strong>, không áp lực mua hàng.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="tel:0325805893"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-black text-sm transition-all hover:opacity-90 active:scale-[0.98]"
                    style={{
                      fontFamily: "var(--font-montserrat)",
                      backgroundColor: "var(--brand-primary)",
                      color: "var(--brand-primary-foreground)",
                      boxShadow: "0 4px 16px rgba(200,169,110,0.3)",
                    }}
                  >
                    <Phone className="w-4 h-4" />
                    Gọi ngay: 0325 805 893
                  </a>
                  <div
                    className="flex items-center gap-2 px-4 py-3 rounded-xl border text-sm"
                    style={{ borderColor: "var(--border)", color: "var(--muted-foreground)" }}
                  >
                    <Clock className="w-4 h-4" style={{ color: "var(--brand-primary)" }} />
                    18:00 – 22:00 · Mỗi ngày
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Divider />

        {/* ══════════════════════════════════════
            LOCATION
        ══════════════════════════════════════ */}
        <section className="pb-20">
          <div
            className="rounded-3xl border p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6"
            style={{
              backgroundColor: "var(--card)",
              borderColor: "var(--border)",
            }}
          >
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 border"
              style={{
                backgroundColor: "rgba(200,169,110,0.1)",
                borderColor: "rgba(200,169,110,0.3)",
              }}
            >
              <MapPin className="w-6 h-6" style={{ color: "var(--brand-primary)" }} />
            </div>
            <div className="flex-1">
              <h3
                className="font-black text-base mb-1"
                style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
              >
                Guitar Sài Gòn – 73 Đường ĐHT 31, P. Tân Hưng Thuận, Quận 12, TPHCM
              </h3>
              <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>
                Phục vụ khách từ{" "}
                {["Quận 12", "Gò Vấp", "Hóc Môn", "Bình Dương", "Thủ Đức"].map((area, i, arr) => (
                  <span key={area}>
                    <strong style={{ color: "var(--foreground)" }}>{area}</strong>
                    {i < arr.length - 1 ? ", " : ""}
                  </span>
                ))}
              </p>
            </div>
            <Link
              href="/lien-he"
              className="shrink-0 inline-flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-sm border transition-all"
              style={{
                fontFamily: "var(--font-montserrat)",
                borderColor: "rgba(200,169,110,0.35)",
                color: "var(--brand-primary)",
                backgroundColor: "rgba(200,169,110,0.06)",
              }}
            >
              Xem bản đồ
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

      </div>
    </main>
  );
}